import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import Cookies from '../providers/Cookies';
import Analytics from '../analytics';
import {
  postBookingBook,
  postBookingBookConcierge,
  postBookingChange,
  postBookingDetails,
  postGoogleBookingDetails,
} from '../api';
import getServiceDateFromResyToken from '../utils/resyToken';

import {
  SUMMARY,
  CONFIRMATION,
  AUTH,
  ADD_CC,
  CHOOSE_CC,
  RESERVATION_PATH_PLACEMENT,
} from '../constants/bookingFlow';

import {
  IDLE,
  PENDING,
  SUCCEEDED,
  FAILED,
} from '../constants/asyncLoadingStatuses';
import getPaymentRequired from '../utils/getPaymentRequired';
import { getNaaAnnouncementThunk } from './announcementsSlice';
import { EVENT_TYPE } from '../components/Announcement/announcementTypes';

// for booking
// postToDetails is called
// which returns a token and all other information needed
// to present to the end user - cancellation policy for example
// token is then used in call to make the booking
// Some possible errors when calling 3/book
// 303, RGS Partner indicated that we should redirect the request
// 402, Payment Required, include a payment method
// 403, concierge. the guest already has a reservation around this time
// 404, not found
// 409, cannot be cancelled after cut off time
// 412, a reservation already exists
// 412 with code 1019 - some items are no longer available

const defaultState = {
  // the heart of the request - commit, config id (is on reservation as token), date, and party size
  // if true(1), creates an invoice for a paid reservation on post to details 3/details
  // note this is the only arg passed into postBookingDetailsThunk for convenience
  // when setting to false, it simply retreives a specified reservation
  commit: 1,
  // which date, example '2022-04-28'
  day: undefined,
  // number of people
  partySize: undefined,
  // service charge as a string percentage - example '20%'
  serviceCharge: undefined,
  // direct mapping to availability
  // no reservations available, no can book
  // must default to true
  canBook: true,

  // used in the payload to signify an affiliated venue
  affId: undefined,

  // set after a successful booking
  // reservation_id - number
  // resy_token - string
  booking: undefined,

  // maintains the state of user selection.
  // a concierge may book for themselves or as concierge
  bookOnBehalfOf: undefined,
  // email: em_address
  // first_name
  // last_name
  // mobile_number - string
  // paymentId - number
  bookOnBehalfOfPayload: undefined,

  // set after a successful concierge booking
  // concierge_resy_token - string
  conciergeBooking: undefined,

  // the return from postToDetails
  // this is a large object which is used for displaying data
  // but since it is not committed, it includes
  // all gratuity options
  details: undefined,

  // Identifies the selection whose details were loaded and whose cart event was sent.
  detailsBookingKey: undefined,
  cartTrackedBookingKey: undefined,

  // the return from postToDetails
  // postToDetails with a commit value of 1
  // this has the token you will use to make the booking
  // and does not include ALL gratuity options,
  // only the option selected by the user
  // book_token
  // value
  detailsCommitted: undefined,

  errorBook: undefined,
  errorDetail: undefined,

  loadingDetails: IDLE,
  loadingBooking: IDLE,

  // arr of
  // category_id
  // item_token
  // item_id
  // name
  // quantity
  items: undefined,

  // id if associated with offer
  offerId: undefined,

  nonce: undefined,

  // if booking is sourced from a partner, like Toast, this is the reference ID
  partnerId: undefined,

  paymentId: undefined,

  // in case of replacing an exisitng reservation
  // 1 or 0
  replace: undefined,

  // time
  // templateId
  // isGda
  // price
  // type
  reservation: undefined,

  // important! we keep track of where the booking is coming from
  sourceId: undefined,

  // UTM attribution source captured at init-time
  utmSource: undefined,

  // string
  // if this exists and call to book, then
  // postBookingChange is used
  // to change the current reservation
  resyToken: undefined,

  // string, used in pathBuilder
  // for navigation
  venueId: undefined,

  // string
  // used in analytics
  venueName: undefined,

  venueCommunicationOptIn: false,

  /**
   * What step are we on?
   * BROWSE, MENU, SUMMARY, BOOK, AUTH, ADD_CC, CONFIRMATION
   */
  step: null,
};

export const isSuccess = (status) => status > 199 && status < 300;

// filter out keys not used in the payload
const getItems = (items) => (
  items?.map((i) => {
    // eslint-disable-next-line camelcase
    const { item_token, quantity } = i;
    return {
      // eslint-disable-next-line camelcase
      item_token,
      quantity,
    };
  })
);

const getRequestPayloadPostToDetails = (state) => {
  const {
    captchaResponse,
    commit,
    day: requestedDay,
    // eslint-disable-next-line camelcase
    partySize: party_size,
    reservation: {
      // eslint-disable-next-line camelcase
      token: config_id,
      availabilityTag,
      roomId,
      startSec,
      seats: reservationPartySize,
    } = {},
    // eslint-disable-next-line camelcase
    serviceCharge: service_charge,
    items,
    // eslint-disable-next-line camelcase
    resyToken: resy_token,
  } = state;
  const day = getServiceDateFromResyToken(config_id, requestedDay);

  let payload;

  if (roomId) {
    payload = {
      commit,
      captcha_token: captchaResponse,
      availability_tag: availabilityTag || undefined,
      room_id: roomId,
      start_secs: startSec,
      struct_items: getItems(items),
      party_size: reservationPartySize,
    };
  } else {
    payload = {
      captcha_token: captchaResponse,
      commit,
      config_id,
      day,
      party_size,
      service_charge,
      struct_items: getItems(items),
      resy_token,
    };
  }

  // remove undefined values
  const keys = Object.keys(payload);
  return keys.reduce(
    (iter, val) => {
      if (typeof payload[val] !== 'undefined') {
        iter[val] = payload[val];
      }
      return iter;
    },
    {}
  );
};

const getRequestPayloadBook = (state) => {
  const {
    affId,
    bookOnBehalfOf,
    bookOnBehalfOfPayload,
    detailsCommitted: {
      book_token: {
        value: bookToken,
      } = {},
    } = {},
    offerId,
    nonce,
    paymentId,
    replace,
    resyToken,
    sourceId,
    utmSource,
    partnerId,
    venueId,
    venueCommunicationOptIn,
    reservation: {
      rwgToken,
    } = {},
  } = state;

  if (!bookToken) { return undefined; }

  const payload = {};
  payload.book_token = bookToken;

  if (bookOnBehalfOf && bookOnBehalfOfPayload) {
    const {
      /* eslint-disable camelcase */
      email: em_address,
      first_name,
      last_name,
      mobile_number,
      /* eslint-enable camelcase */
    } = bookOnBehalfOfPayload;

    payload.struct_guest = {
      em_address,
      first_name,
      last_name,
      mobile_number,
    };
  }

  if (resyToken) {
    payload.resy_token = resyToken;
  }

  if (replace) {
    payload.replace = 1;
  }

  if (paymentId && paymentId !== -1 && !bookOnBehalfOf) {
    payload.struct_payment_method = {
      id: paymentId,
    };
  }

  // one time use credit cards
  if (nonce) {
    payload.struct_payment_method = {
      nonce,
    };
  }

  if (affId) {
    payload.affiliate_id = affId;
  }
  if (offerId) {
    payload.offer_id = offerId;
  }
  if (sourceId) {
    payload.source_id = sourceId;
  }
  if (partnerId && sourceId) {
    payload.struct_reference = {
      provider: sourceId,
      value: partnerId,
    };
  }

  // This is for tracking demand api partner integrations reservations in core db.
  // If there is utmSource then provider and source_id is set to utmSource
  // partnerId should also be available in that case but if not
  // then use utmSource.
  if (utmSource) {
    payload.source_id = utmSource;
    payload.struct_reference = {
      provider: utmSource,
      value: partnerId || utmSource,
    };
  }

  payload.venue_marketing_opt_in = venueCommunicationOptIn ? 1 : 0;
  if (bookOnBehalfOf) {
    payload.venue_marketing_opt_in = null;
  }

  // Reserve with Google payload data
  const rwgCookie = Cookies.get('_rwg');
  if (rwgCookie) {
    // for RwG tokens stored in a cookie
    const [merchantId, token] = rwgCookie.split('~');
    if (token && merchantId) {
      payload.rwg_token = token;
      payload.merchant_changed = (venueId === merchantId) ? 2 : 1;
    }
  } else if (rwgToken) {
    // for RwG tokens in the current booking session data
    payload.rwg_token = rwgToken;
    payload.merchant_changed = 2;
  }

  return payload;
};

const getBookingFunc = (state) => {
  const {
    bookOnBehalfOf,
    resyToken,
  } = state;

  if (bookOnBehalfOf) {
    return postBookingBookConcierge;
  } if (resyToken) {
    return postBookingChange;
  }
  return postBookingBook;
};

const book = (state) => {
  const func = getBookingFunc(state);
  const payload = getRequestPayloadBook(state);
  if (payload) {
    return func(payload);
  }
  return Promise.reject(
    new Error('The payload could not be created. Missing book token.')
  );
};

// makes the invoice/lock
const postGoogleBookingDetailsThunk = createAsyncThunk(
  'booking/postGoogleBookingDetailsStatus',
  // all data needed to make the call should be in state
  (commit = 0, { getState }) => (postGoogleBookingDetails(
    getRequestPayloadPostToDetails(
      { ...getState()?.booking, ...getState()?.captchaResponse, commit }
    )
  ))
);
// makes the invoice/lock
const postBookingDetailsThunk = createAsyncThunk(
  'booking/postBookingDetailsStatus',
  // all data needed to make the call should be in state
  (commit = 0, { getState }) => (postBookingDetails(
    getRequestPayloadPostToDetails(
      { ...getState()?.booking, ...getState()?.captchaResponse, commit }
    )
  ))
);

// the api is not consistent in error shape
// esp where code and message are located
const normalizeError = (response = {}) => {
  const { data, status, statusText } = response;
  let { code, message } = response;
  code = code || data?.code;
  message = data?.data?.struct_guest || message || data?.message;
  return {
    code, data, message, status, statusText,
  };
};

// books the reservation
const postBookingBookThunk = createAsyncThunk(
  'booking/postBookingBookThunkStatus',
  async (foo, { getState, rejectWithValue }) => {
    const bookingState = { ...getState()?.booking };
    const response = await book(bookingState);
    const success = isSuccess(normalizeError(response).status);
    if (!success) {
      return rejectWithValue(normalizeError(response));
    }

    return response;
  }
);

const getAnalyticsEventData = (state) => {
  const { reservation, details, partySize } = state;
  const {
    venue, eventId, title, type,
  } = reservation;
  const eventData = {
    category: 'Event',
    venue,
    event_id: eventId,
    event_name: title,
    package_name: reservation?.type,
    package_price: parseInt(reservation.price, 10),
    party_size: partySize,
    gratuity: details?.payment?.amounts?.service_charge?.value || '0%',
    beverage_pairing: !!details?.payment?.amounts?.add_ons,
    lead_time: moment().format('YYYY-MM-DDTHH:MM:SS'),
  };
  if (type === 'seated') {
    eventData.time_slot = moment(reservation?.time, 'YYYY-MM-DD HH:MM:SS').format('HH:MM');
  }
  return eventData;
};

const getReservationKey = (reservation) => {
  if (reservation?.token) return reservation.token;
  if (reservation?.roomId) {
    return `${reservation.roomId}:${reservation.rwgToken || ''}:${reservation.startSec || reservation.time || ''}:${reservation.seats || ''}`;
  }
  return undefined;
};

export const getBookingIdentity = (booking) => getReservationKey(booking?.reservation);

export const reducers = {
  setDetails: (state, action) => {
    state.details = action.payload;
    state.detailsBookingKey = getBookingIdentity(state);
  },
  markAddToCartTracked: (state, action) => { state.cartTrackedBookingKey = action.payload; },
  setDetailsCommitted: (state, action) => { state.detailsCommitted = action.payload; },
  book,
  reset: () => (defaultState),
  resetBookingError: (state) => { state.errorBook = undefined; state.loadingBooking = IDLE; },
  resetPayment: (state) => { state.paymentId = undefined; state.nonce = undefined; },
  setAffId: (state, action) => { state.affId = action.payload; },
  setBookOnBehalfOf: (state, action) => { state.bookOnBehalfOf = action.payload; },
  setBookOnBehalfOfPayload: (state, action) => { state.bookOnBehalfOfPayload = action.payload; },
  setCanBook: (state, action) => { state.canBook = action.payload; },
  setCaptchaResponse: (state, action) => { state.captchaResponse = action.payload; },
  setDay: (state, action) => { state.day = action.payload; },
  setItems: (state, action) => { state.items = action.payload; },
  setNonce: (state, action) => { state.nonce = action.payload; },
  setOfferId: (state, action) => { state.offerId = action.payload; },
  setPartySize: (state, action) => { state.partySize = action.payload; },
  setPaymentId: (state, action) => { state.paymentId = action.payload; },
  setReplace: (state, action) => { state.replace = action.payload; },
  setReservation: (state, action) => {
    if (getReservationKey(state.reservation) !== getReservationKey(action.payload)) {
      state.details = undefined;
      state.detailsBookingKey = undefined;
      state.venueName = undefined;
    }
    state.reservation = action.payload;
  },
  setResyToken: (state, action) => { state.resyToken = action.payload; },
  setServiceCharge: (state, action) => { state.serviceCharge = action.payload; },
  setSourceId: (state, action) => { state.sourceId = action.payload; },
  setUtmSource: (state, action) => { state.utmSource = action.payload; },
  setPartnerId: (state, action) => { state.partnerId = action.payload; },
  setVenueId: (state, action) => { state.venueId = action.payload; },
  setVenueName: (state, action) => { state.venueName = action.payload; },
  toggleVenueCommunicationOptIn: (state) => { state.venueCommunicationOptIn = !state.venueCommunicationOptIn; },
  // results in navigation
  setBookingFlowState: (state, action) => { state.step = action.payload; },
};

const bookingSlice = createSlice({
  name: 'booking',
  initialState: defaultState,
  reducers,
  extraReducers: (builder) => {
    builder.addCase(postBookingDetailsThunk.pending, (state) => {
      state.errorDetail = undefined;
      state.loadingDetails = PENDING;
    });
    builder.addCase(postBookingDetailsThunk.fulfilled, (state, action) => {
      if (action.payload.status >= 400) {
        state.loadingDetails = FAILED;
        state.errorDetail = action.payload.data;
      } else {
        state.loadingDetails = SUCCEEDED;
      }
    });
    builder.addCase(postBookingDetailsThunk.rejected, (state, action) => {
      state.loadingDetails = FAILED;
      state.errorDetail = action.payload;
    });

    builder.addCase(postGoogleBookingDetailsThunk.pending, (state) => {
      state.errorDetail = undefined;
      state.loadingDetails = PENDING;
    });
    builder.addCase(postGoogleBookingDetailsThunk.fulfilled, (state, action) => {
      if (action.meta.requestStatus !== 'fulfilled') {
        state.loadingDetails = FAILED;
        state.errorDetail = action.payload;
      } else {
        state.loadingDetails = SUCCEEDED;
      }
    });
    builder.addCase(postGoogleBookingDetailsThunk.rejected, (state, action) => {
      state.loadingDetails = FAILED;
      state.errorDetail = action.payload;
    });

    builder.addCase(postBookingBookThunk.pending, (state) => {
      state.loadingBooking = PENDING;
      state.errorBook = undefined;
    });
    builder.addCase(postBookingBookThunk.fulfilled, (state, action) => {
      state.loadingBooking = SUCCEEDED;
      state.booking = action.payload.data;
    });
    builder.addCase(postBookingBookThunk.rejected, (state, action) => {
      state.loadingBooking = FAILED;
      state.errorBook = action.payload;
      state.nonce = undefined;
    });
  },
});

const {
  setBookingFlowState: setBookingFlow,
} = bookingSlice.actions;

const fireOpenAiAnalyticsEvent = (sourceId, bookingInfo, status, partnerId, action) => {
  if (sourceId === 'oai_widget') {
    const reservation = bookingInfo?.reservation;

    const openAiEventData = {
      status,
      src: sourceId,
      partnerId,
      timeslot: reservation?.time,
      date: bookingInfo?.day,
      tabletype: reservation?.type,
      serviceId: reservation?.serviceTypeId,
      partysize: bookingInfo?.partySize,
      reservationId: action?.payload?.data?.reservation_id,
    };

    Analytics().event(EVENT_TYPE.OAI_DONE_CLICK, openAiEventData);
  }
};

/**
 * attempts to book, checking for logged in status, for bookOnBehalf
 * and payment required
 */
const attemptBook = ({ isAuthenticated = false, cardsOnFile = false }) => (dispatch, getState) => {
  if (!isAuthenticated) {
    return dispatch(setBookingFlow(AUTH));
  }

  const {
    bookOnBehalfOf,
    nonce,
    paymentId,
    detailsCommitted,
    loadingBooking,
    sourceId,
    partnerId,
  } = getState()?.booking;

  if (loadingBooking === PENDING) {
    // eslint-disable-next-line consistent-return
    return;
  }

  if (bookOnBehalfOf && getPaymentRequired(detailsCommitted) && !nonce) {
    return dispatch(setBookingFlow(ADD_CC));
  } if (!bookOnBehalfOf && getPaymentRequired(detailsCommitted) && !paymentId) {
    if (cardsOnFile) {
      return dispatch(setBookingFlow(CHOOSE_CC));
    }
    return dispatch(setBookingFlow(ADD_CC));
  }

  return dispatch(postBookingBookThunk())
    .then(
      (action) => {
        const status = action.payload?.status || action.payload?.data?.status;

        if (isSuccess(status)) {
          const bookingInfo = getState().booking;
          // eslint-disable-next-line camelcase
          const { em_address = '', mobile_number = '' } = getState().user?.userData ?? {};
          Analytics().purchase(action.payload.data?.resy_token, {
            /** the 'name' prop is for floodlight tagging */
            name: 'Reservation placed',
            ...bookingInfo,
            em_address,
            mobile_number,
            searchSessionId: getState().venueSearch?.metadata?.searchSessionId,
          });

          fireOpenAiAnalyticsEvent(sourceId, bookingInfo, status, partnerId, action);

          if (
            !bookingInfo?.reservation?.isEvent
            && !bookOnBehalfOf
            // for chatGPT; when openai is the source, we skip NAA
            && sourceId !== 'oai_widget'
          ) {
            dispatch(getNaaAnnouncementThunk({ uuid: getState().resyConfig.deviceId, page: 'reservation_path', location_id: 'ny' }))
              .then(() => {
                if (getState().naaAnnouncement?.offerData?.show_placement) {
                  const { header } = getState().naaAnnouncement?.offerData;
                  const id = getState().naaAnnouncement?.id;
                  Analytics().event(EVENT_TYPE.VIEW_ANNOUNCEMENT, {
                    context: 'reservation path',
                    type: 'booking',
                    title: header,
                    id,
                  });
                  dispatch(setBookingFlow(RESERVATION_PATH_PLACEMENT));
                } else {
                  // when API return 422 or empty offerData in response.
                  dispatch(setBookingFlow(CONFIRMATION));
                }
              });
          } else {
            dispatch(setBookingFlow(CONFIRMATION));
          }
        } else {
          dispatch(setBookingFlow(SUMMARY));
        }
      },
      () => dispatch(setBookingFlow(SUMMARY))
    );
};

const {
  setDetails: setDetailsInState,
  setDetailsCommitted: setDetailsCommittedInState,
} = bookingSlice.actions;

// does not commit (0) but retrieves gratuity
// The two branches unwrap differently on purpose: the Google call goes through
// resyApiPost, whose interceptor already returns response.data, while the legacy
// call keeps the axios envelope. Both store the same /3/details response body.
const getDetailsThunk = () => (dispatch, getState) => {
  const { reservation } = getState()?.booking;
  const bookingIdentity = getBookingIdentity(getState()?.booking);
  if (reservation?.roomId) {
    return dispatch(postGoogleBookingDetailsThunk(0))
      .then(
        (action) => {
          if (postGoogleBookingDetailsThunk.fulfilled.match(action)
              && getBookingIdentity(getState()?.booking) === bookingIdentity) {
            dispatch(setDetailsInState(action.payload));
          }
        }
      );
  }

  return dispatch(postBookingDetailsThunk(0))
    .then(
      (action) => {
        const status = action.payload?.status || action.payload?.data?.status;
        if (isSuccess(status) && getBookingIdentity(getState()?.booking) === bookingIdentity) {
          dispatch(setDetailsInState(action.payload.data));
        }
      }
    );
};

// commits (1) including committing gratuity, does not retrieve all gratuity options
const commitDetailsThunk = () => (dispatch, getState) => {
  const { reservation } = getState()?.booking;
  if (reservation?.roomId) {
    return dispatch(postGoogleBookingDetailsThunk(1))
      .then(
        (action) => {
          dispatch(setDetailsCommittedInState(action.payload));
        }
      );
  }

  return dispatch(postBookingDetailsThunk(1))
    .then(
      (action) => {
        const status = action.payload?.status || action.payload?.data?.status;
        if (isSuccess(status)) {
          dispatch(setDetailsCommittedInState(action.payload.data));
        }
      }
    );
};

export {
  attemptBook,
  getAnalyticsEventData,
  getBookingFunc,
  getDetailsThunk,
  commitDetailsThunk,
  getItems,
  getRequestPayloadBook,
  getRequestPayloadPostToDetails,
  postBookingDetailsThunk,
  postGoogleBookingDetailsThunk,
  postBookingBookThunk,
};

export const {
  reset,
  resetBookingError,
  resetPayment,
  setAffId,
  setBookOnBehalfOf,
  setBookOnBehalfOfPayload,
  setCanBook,
  setCaptchaResponse,
  setDay,
  setDetails,
  markAddToCartTracked,
  setDetailsCommitted,
  setItems,
  setNonce,
  setOfferId,
  setPartySize,
  setPaymentId,
  setReplace,
  setReservation,
  setResyToken,
  setServiceCharge,
  setSourceId,
  setUtmSource,
  setPartnerId,
  setVenueId,
  setVenueName,
  toggleVenueCommunicationOptIn,
  setBookingFlowState,
  bookingFlowBrowseAdvance,
  bookingFlowSummaryAdvance,
} = bookingSlice.actions;

export default bookingSlice.reducer;
